/**
 * SAM-626 (item 4) — season bounds, matching and overlap validation for
 * `service.pricing.seasons`. Pure TypeScript port of
 * `functions/booking/pricing_seasons.py` (sam-lambda-functions, PR #433): the editor must
 * flag exactly what the CRUD save would reject, so the year-expansion window, the leap-day
 * clamp and the sweep are copied as-is. Keep the two in step.
 *
 * Stored shapes:
 *   fixed      { id, start_date: "2026-12-15", end_date: "2027-04-15" }                (no every_year key)
 *   recurring  { id, every_year: true, start_date: "12-15", end_date: "01-15" }        (MM-DD, inclusive)
 *
 * A recurring season whose start is after its end crosses the year end. `02-29` is a valid bound
 * and clamps to `02-28` in non-leap years (start and end alike).
 *
 * Dates are handled as UTC day numbers (integer days since the epoch) so there is no timezone or
 * DST drift.
 */

import { formatDay } from './dateUtils';

export type SeasonErrorCode = 'invalid_season' | 'season_overlap';

export class SeasonError extends Error {
  code: SeasonErrorCode;
  details: { season?: string | null; seasons?: Array<string | null> };

  constructor(message: string, code: SeasonErrorCode = 'invalid_season', details: SeasonError['details'] = {}) {
    super(message);
    this.name = 'SeasonError';
    this.code = code;
    this.details = details;
  }
}

/** Minimum shape the helpers read (a payload season, or a form row mapped to one). */
export interface SeasonBounds {
  id?: string | null;
  every_year?: unknown;
  start_date?: unknown;
  end_date?: unknown;
}

export type MonthDay = readonly [month: number, day: number];

// Years used to expand recurring x recurring: two non-leap + a leap + one more, so a leap-day
// clamp difference and a year-crossing range are both exercised.
const RECURRING_WINDOW = [2023, 2024, 2025, 2026];
const MAX_FIXED_SPAN_YEARS = 400;
const DAY_MS = 86_400_000;

/** Days per month with February at 29 (2000 is a leap year): the picker's day-count table. */
export const MAX_DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31] as const;

export const isRecurring = (season: SeasonBounds | null | undefined): boolean =>
  !!season && typeof season === 'object' && season.every_year === true;

const seasonId = (season: SeasonBounds): string | null => (season && season.id != null ? String(season.id) : null);

/** UTC day number of a real calendar date, or null when (y, m, d) does not exist. */
function dayNumber(year: number, month: number, day: number): number | null {
  const date = new Date(Date.UTC(year, month - 1, day));
  // `Date.UTC` maps years 0-99 to 1900-1999; setUTCFullYear keeps the year as given.
  date.setUTCFullYear(year);
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;
  return Math.round(date.getTime() / DAY_MS);
}

const dayFromIso = (iso: unknown): number | null => {
  if (typeof iso !== 'string') return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  return m ? dayNumber(Number(m[1]), Number(m[2]), Number(m[3])) : null;
};

/** `"MM-DD"` -> `[month, day]` (02-29 allowed) or null. */
export function parseMonthDay(value: unknown): MonthDay | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  if (!/^\d{2}-\d{2}$/.test(text)) return null;
  const month = Number(text.slice(0, 2));
  const day = Number(text.slice(3));
  return dayNumber(2000, month, day) === null ? null : [month, day];
}

/** The concrete day of a bound in `year`; 02-29 clamps to 02-28 in non-leap years. */
function mdInYear([month, day]: MonthDay, year: number): number {
  const exact = dayNumber(year, month, day);
  if (exact !== null) return exact;
  return dayNumber(year, 2, 28) as number;
}

const mdCompare = (a: MonthDay, b: MonthDay): number => (a[0] - b[0]) || (a[1] - b[1]);

type FixedBounds = { recurring: false; start: number; end: number };
type RecurringBounds = { recurring: true; start: MonthDay; end: MonthDay };

// Type guards: the project is not `strict`, so `recurring` does not narrow the union by itself.
const isFixedBounds = (b: FixedBounds | RecurringBounds): b is FixedBounds => !b.recurring;

/** Bounds of a season or `SeasonError('invalid_season')`. Mirrors `season_bounds`. */
export function seasonBounds(season: SeasonBounds): FixedBounds | RecurringBounds {
  const sid = seasonId(season);
  if (isRecurring(season)) {
    const start = parseMonthDay(season.start_date);
    const end = parseMonthDay(season.end_date);
    if (!start || !end) {
      throw new SeasonError('Recurring season needs start_date and end_date as MM-DD', 'invalid_season', { season: sid });
    }
    return { recurring: true, start, end };
  }
  const start = dayFromIso(season.start_date);
  const end = dayFromIso(season.end_date);
  if (start === null || end === null) {
    throw new SeasonError('Season needs valid ISO start_date and end_date', 'invalid_season', { season: sid });
  }
  if (end < start) {
    throw new SeasonError('Season end_date is before start_date', 'invalid_season', { season: sid });
  }
  return { recurring: false, start, end };
}

/** Concrete inclusive interval of a recurring season that STARTS in `year`. */
function recurringInterval(start: MonthDay, end: MonthDay, year: number): [number, number] {
  const lo = mdInYear(start, year);
  return mdCompare(start, end) <= 0 ? [lo, mdInYear(end, year)] : [lo, mdInYear(end, year + 1)];
}

/** Whether `season` (fixed or recurring, inclusive bounds) contains the ISO day. Invalid -> false. */
export function seasonContains(season: SeasonBounds, isoDay: string): boolean {
  const day = dayFromIso(isoDay);
  if (day === null) return false;
  let bounds: FixedBounds | RecurringBounds;
  try {
    bounds = seasonBounds(season);
  } catch {
    return false;
  }
  if (isFixedBounds(bounds)) return bounds.start <= day && day <= bounds.end;
  const year = new Date(day * DAY_MS).getUTCFullYear();
  // A crossing season that started last year may still cover early January.
  return [year - 1, year].some((y) => {
    const [lo, hi] = recurringInterval(bounds.start, bounds.end, y);
    return lo <= day && day <= hi;
  });
}

/**
 * The season containing `isoDay` (first 10 chars are used) or null. Fixed-date seasons win over
 * recurring ones (list order within each group), like `find_season`. Unlike the backend it never
 * throws on a malformed season: an invalid one simply does not match.
 */
export function findSeasonForDate<T extends SeasonBounds>(seasons: readonly T[] | null | undefined, isoDay: string | null | undefined): T | null {
  const day = typeof isoDay === 'string' && isoDay.length >= 10 ? isoDay.slice(0, 10) : null;
  if (!day || !Array.isArray(seasons)) return null;
  const dicts = seasons.filter((s) => s && typeof s === 'object');
  const ordered = [...dicts.filter((s) => !isRecurring(s)), ...dicts.filter((s) => isRecurring(s))];
  return ordered.find((s) => seasonContains(s, day)) ?? null;
}

/** Concrete `[start, end]` intervals of a season over `years` (fixed: exactly one). */
function expand(bounds: FixedBounds | RecurringBounds, years: number[]): Array<[number, number]> {
  if (isFixedBounds(bounds)) return [[bounds.start, bounds.end]];
  return years.map((y) => recurringInterval(bounds.start, bounds.end, y));
}

const yearOf = (day: number): number => new Date(day * DAY_MS).getUTCFullYear();

/** The year window the backend expands over: the fixed base window plus every year a fixed season touches. */
function expansionYears(allBounds: Array<FixedBounds | RecurringBounds>): number[] {
  const years = new Set<number>(RECURRING_WINDOW);
  const fixedYears: number[] = [];
  allBounds.forEach((b) => {
    if (isFixedBounds(b)) fixedYears.push(yearOf(b.start), yearOf(b.end));
  });
  if (fixedYears.length) {
    const min = Math.min(...fixedYears);
    const max = Math.max(...fixedYears);
    const lo = min - 1;
    const hi = Math.min(max + 1, min + MAX_FIXED_SPAN_YEARS);
    for (let y = lo; y <= hi; y += 1) years.add(y);
  }
  return [...years].sort((a, b) => a - b);
}

/**
 * Validate a season list exactly like `validate_seasons`. Throws `SeasonError`:
 * `invalid_season` (bad/reversed/malformed dates, non-object entries) or `season_overlap`
 * (two seasons share a day in some year; bounds inclusive) with `details.seasons = [idA, idB]`.
 */
export function validateSeasons(seasons: readonly SeasonBounds[] | null | undefined): void {
  const list = seasons || [];
  if (!Array.isArray(list)) throw new SeasonError('seasons must be a list', 'invalid_season');
  list.forEach((s) => {
    if (!s || typeof s !== 'object' || Array.isArray(s)) throw new SeasonError('Each season must be an object', 'invalid_season');
  });
  const bounds = list.map(seasonBounds); // raises invalid_season first
  const years = expansionYears(bounds);

  const intervals: Array<{ start: number; end: number; index: number }> = [];
  bounds.forEach((b, index) => {
    expand(b, years).forEach(([start, end]) => intervals.push({ start, end, index }));
  });
  intervals.sort((a, b) => (a.start - b.start) || (a.end - b.end));
  let reachEnd: number | null = null;
  let reachIndex: number | null = null;
  for (const { start, end, index } of intervals) {
    if (reachEnd !== null && start <= reachEnd && index !== reachIndex) {
      throw new SeasonError('Seasons overlap', 'season_overlap', {
        seasons: [seasonId(list[reachIndex as number]), seasonId(list[index])],
      });
    }
    if (reachEnd === null || end > reachEnd) {
      reachEnd = end;
      reachIndex = index;
    }
  }
}

/**
 * EVERY overlapping pair (indexes into `seasons`), for the editor to flag each row. Same
 * expansion window and the same interval test as `validateSeasons`, checked pairwise instead of
 * stopping at the first pair. Rows that are not valid seasons are skipped (the per-row
 * validation reports those).
 */
export function findSeasonOverlaps(seasons: readonly SeasonBounds[]): Array<[number, number]> {
  const valid: Array<{ index: number; bounds: FixedBounds | RecurringBounds }> = [];
  seasons.forEach((s, index) => {
    try {
      valid.push({ index, bounds: seasonBounds(s) });
    } catch {
      /* reported per row */
    }
  });
  const years = expansionYears(valid.map((v) => v.bounds));
  const expanded = valid.map((v) => ({ index: v.index, intervals: expand(v.bounds, years) }));
  const pairs: Array<[number, number]> = [];
  for (let i = 0; i < expanded.length; i += 1) {
    for (let j = i + 1; j < expanded.length; j += 1) {
      const hit = expanded[i].intervals.some(([a1, a2]) => expanded[j].intervals.some(([b1, b2]) => a1 <= b2 && b1 <= a2));
      if (hit) pairs.push([expanded[i].index, expanded[j].index]);
    }
  }
  return pairs;
}

// ─── Editor helpers (no backend counterpart) ─────────────────────────────────

export const pad2 = (n: number): string => String(n).padStart(2, '0');

export const formatMonthDay = ([month, day]: MonthDay): string => `${pad2(month)}-${pad2(day)}`;

export const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

/** "Dec 15" for a `MM-DD` string, or '' when it is not one. */
export function describeMonthDay(value: string): string {
  const md = parseMonthDay(value);
  return md ? `${MONTH_NAMES[md[0] - 1]} ${md[1]}` : '';
}

/**
 * A season or closure bound with the month written out (SAM-739): `MM-DD` (every year) reads
 * «Dec 15», a calendar day `YYYY-MM-DD` reads «Dec 15, 2026». Anything else is shown as is.
 */
export function describeDayBound(value: string): string {
  return describeMonthDay(value) || formatDay(value) || value;
}

/** A from–to range of bounds, «Dec 15 → Jan 15»; one bound when both are the same day. */
export function describeDayRange(start: string, end: string): string {
  return start === end ? describeDayBound(start) : `${describeDayBound(start)} → ${describeDayBound(end)}`;
}

/** True when a recurring range runs over the year end (start after end, e.g. 12-15 -> 01-15). */
export function crossesYearEnd(start: string, end: string): boolean {
  const s = parseMonthDay(start);
  const e = parseMonthDay(end);
  return !!s && !!e && mdCompare(s, e) > 0;
}

/** True when a `MM-DD` bound is Feb 29. */
export const isLeapDayBound = (value: string): boolean => {
  const md = parseMonthDay(value);
  return !!md && md[0] === 2 && md[1] === 29;
};

/** `YYYY-MM-DD` -> `MM-DD` (or '' when it is not an ISO date). */
export function isoToMonthDay(iso: string): string {
  const day = dayFromIso(iso);
  return day === null ? '' : iso.trim().slice(5, 10);
}

const isoFromDay = (day: number): string => {
  const d = new Date(day * DAY_MS);
  return `${String(d.getUTCFullYear()).padStart(4, '0')}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
};

/**
 * The next real occurrence of a `MM-DD` on or after the ISO `from` day, as ISO. Feb 29 resolves
 * to the next leap year's Feb 29 (a fixed season needs a date that exists). '' for a bad input.
 */
export function nextOccurrenceIso(monthDay: string, from: string): string {
  const md = parseMonthDay(monthDay);
  const fromDay = dayFromIso(from);
  if (!md || fromDay === null) return '';
  const startYear = yearOf(fromDay);
  for (let y = startYear; y <= startYear + 8; y += 1) {
    const d = dayNumber(y, md[0], md[1]);
    if (d !== null && d >= fromDay) return isoFromDay(d);
  }
  return '';
}

/** Today as `YYYY-MM-DD` (local calendar day). */
export function todayIso(now: Date = new Date()): string {
  return `${String(now.getFullYear()).padStart(4, '0')}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
}
